/**
 * Live Tracker Service
 * Monitors slide changes and queues reprocessing automatically
 */

import { LiveTrackingStorage, SlideTrackingSnapshot, DeckTrackingState, SlideIndexingStatus } from './liveTrackingStorage';
import { BackendClient, backendClient } from '../communication/backendClient';
import { WS_UPDATE_TYPES } from '../communication/websocketEvents';
import { getOrCreatedocumentId } from '../documentIdManager';
import { getMasterInfoInContext } from '../slideMasterExtractor';
import { SlideCapture } from '../capture/slideCapture';
import { slideId, layoutId, masterId } from '../../utils/idNormalizer';
import { getDeckRegistry } from './deckRegistry';
import GlobalStateManager from '../../main/globals/global-state/globalStateManager';
import type { IDocumentTracker } from '@carousel-office/agent-runtime';

/**
 * Parse slide ID: Office.js returns "257#0" format
 * Normalizes to "slide_257" format for consistency
 */
function parseSlideId(rawSlideId: string | number): string {
  return slideId(rawSlideId);
}

/**
 * Parse layout ID: Office.js returns "2147483951#0" format
 * Normalizes to "layout_2147483951" format for consistency
 */
function parseLayoutId(rawLayoutId: string | number): string {
  return layoutId(rawLayoutId);
}

const FAILED_DESCRIPTION = 'processing failed';
const NON_INDEXED_PLACEHOLDER_DESCRIPTIONS = new Set([
  '',
  'description unavailable',
  'no description available',
  'loading description...',
]);
const SINGLE_SLIDE_INDEXING_THRESHOLD = 1;
const RECONCILIATION_QUEUE_BATCH_SIZE = 10;
const QUEUE_DEDUPE_WINDOW_MS = 5000;

function normalizeShortDescription(value?: string): string {
  return (value || '').trim().toLowerCase();
}

function isFailedDescription(value?: string): boolean {
  return normalizeShortDescription(value) === FAILED_DESCRIPTION;
}

function needsBackfillDescription(value?: string): boolean {
  const normalized = normalizeShortDescription(value);
  return NON_INDEXED_PLACEHOLDER_DESCRIPTIONS.has(normalized);
}

export interface ChangeDetection {
  type: 'content_change' | 'slides_added' | 'slides_deleted' | 'structural_check';
  documentId: string;
  slideIds: string[];
  slideIndexes: number[];
  timestamp: number;
  details?: string;
}

export interface LiveTrackerStatus {
  isEnabled: boolean;
  trackedDecks: number;
  totalSnapshots: number;
  changesDetected: number;
  lastActivity?: number;
}

/**
 * Entry for tracking slide description in active layout state
 */
export interface SlideDescriptionEntry {
  slide_id: string;
  shortDescription: string;
  isLoading?: boolean;
}

/**
 * Active presentation state maintained during tracking
 */
export interface ActivePresentationState {
  documentId: string;
  layouts: Map<string, SlideDescriptionEntry>;  // layoutId -> current slide info
}

type ChangeDetectionHandler = (detection: ChangeDetection) => void;

export class LiveTracker implements IDocumentTracker<LiveTrackerStatus> {
  private storage: LiveTrackingStorage;
  private backendClient: BackendClient;
  private slideCapture: SlideCapture;
  private isEnabled: boolean = false;
  private eventHandlerRegistered: boolean = false;
  private changeHandlers: Set<ChangeDetectionHandler> = new Set();
  
  // Track navigation for debouncing
  private lastSelectionTime: number = 0;
  private lastSelectedSlide?: { documentId: string; slideId: string };
  private selectionTimer?: number;
  private rapidNavThreshold: number = 1000; // 1 second for "rapid" navigation
  
  // Track changes detected
  private changesDetected: number = 0;

  // Active presentation state - maintained during tracking
  private activePresentation: ActivePresentationState | null = null;
  private inFlightQueueFingerprints: Map<string, Promise<void>> = new Map();
  private recentQueueFingerprints: Map<string, number> = new Map();

  constructor() {
    this.storage = new LiveTrackingStorage();
    // Use singleton backendClient to share WebSocket connection with other services (e.g., ragUploadService)
    this.backendClient = backendClient;
    // SlideCapture shares the same storage instance
    this.slideCapture = new SlideCapture(this.storage);
  }

  /**
   * Global setting (defaults to ON for backward compatibility).
   */
  private getGlobalIndexingEnabled(): boolean {
    try {
      const globalState = GlobalStateManager.getInstance();
      const generalSettings = globalState.getGeneralSettings();
      return generalSettings.liveSlideIndexingEnabled !== false;
    } catch (error) {
      console.warn('[LiveTracker] Could not read global indexing setting, defaulting to ON:', error);
      return true;
    }
  }

  /**
   * Precedence rule:
   * 1) Deck-level override when explicitly set
   * 2) Otherwise global setting
   */
  private resolveEffectiveDeckIndexing(
    deckState?: DeckTrackingState
  ): { enabled: boolean; source: 'deck' | 'global' } {
    if (deckState && typeof deckState.indexingEnabled === 'boolean') {
      return { enabled: deckState.indexingEnabled, source: 'deck' };
    }
    return { enabled: this.getGlobalIndexingEnabled(), source: 'global' };
  }

  async initialize(): Promise<void> {
    console.log('[LiveTracker] Initializing storage...');
    await this.storage.initialize();
    await this.slideCapture.initialize();
    console.log('[LiveTracker] ✅ Storage initialized');
  }

  /**
   * Start live tracking
   */
  async start(): Promise<void> {
    if (this.isEnabled) {
      console.log('[LiveTracker] Already running');
      return;
    }

    console.log('[LiveTracker] Starting live tracker...');
    this.isEnabled = true;
    this.changesDetected = 0;

    try {
      // Register event handlers if not already done
      if (!this.eventHandlerRegistered) {
        console.log('[LiveTracker] Registering event handlers...');
        await this.registerEventHandlers();
        console.log('[LiveTracker] ✅ Event handlers registered');
      } else {
        console.log('[LiveTracker] Event handlers already registered');
      }

      // Initialize tracking for current presentation
      console.log('[LiveTracker] Initializing current deck...');
      await this.initializeCurrentDeck();
      console.log('[LiveTracker] ✅ Live tracker started successfully');
    } catch (error) {
      console.error('[LiveTracker] ❌ Failed to start:', error);
      this.isEnabled = false;
      throw error;
    }
  }

  /**
   * Stop live tracking
   */
  async stop(): Promise<void> {
    this.isEnabled = false;
    
    if (this.selectionTimer) {
      window.clearTimeout(this.selectionTimer);
      this.selectionTimer = undefined;
    }

    console.log('[LiveTracker] ⏹️ Live tracker stopped');
  }

  /**
   * Register Office.js event handlers
   */
  private async registerEventHandlers(): Promise<void> {
    // GA event: DocumentSelectionChanged (Common API)
    await new Promise<void>((resolve, reject) => {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        (eventArgs) => this.onSelectionChanged(eventArgs),
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log('Live tracker: DocumentSelectionChanged handler registered');
            this.eventHandlerRegistered = true;
            resolve();
          } else {
            console.error('Failed to register selection handler:', result.error?.message);
            reject(result.error);
          }
        }
      );
    });

    // Preview event: Presentation.onSlideSelectionChanged (PowerPoint-specific API)
    try {
      await PowerPoint.run(async (context) => {
        const presentation = context.presentation;
        if ((presentation as any).onSlideSelectionChanged) {
          (presentation as any).onSlideSelectionChanged.add((args: any) => {
            console.log('[LiveTracker] onSlideSelectionChanged (preview):', args);
            this.onSelectionChanged(args);
          });
          await context.sync();
          console.log('Live tracker: onSlideSelectionChanged (preview) handler registered');
        }
      });
    } catch (error) {
      console.log('Live tracker: onSlideSelectionChanged not available (preview API):', (error as Error).message);
    }
  }

  /**
   * Initialize tracking for the current deck
   */
  private async initializeCurrentDeck(): Promise<void> {
    console.log('[LiveTracker] initializeCurrentDeck() called');
    
    try {
      // Get deck ID OUTSIDE of PowerPoint.run to avoid nested context issues
      const documentId = await getOrCreatedocumentId();
      console.log('[LiveTracker] ✅ Got deck ID:', documentId);
      
      // Connect WebSocket for real-time updates from backend
      this.connectWebSocket(documentId);

      // Now get presentation info in a separate context
      let deckName = 'Untitled';
      let slideIds: string[] = [];
      
      await PowerPoint.run(async (context) => {
        console.log('[LiveTracker] Loading presentation data...');
        const presentation = context.presentation;
        presentation.load('title');
        const slides = presentation.slides;
        slides.load('items');
        await context.sync();

        // Get filename from document URL (full path like "C:\Users\...\file.pptx")
        const documentUrl = Office.context.document.url;
        console.log('[LiveTracker] Document URL:', documentUrl);
        console.log('[LiveTracker] Presentation title property:', presentation.title);
        
        if (documentUrl) {
          // Extract filename from path (works for both Windows \ and Unix /)
          const pathParts = documentUrl.split(/[/\\]/);
          const fullFilename = pathParts[pathParts.length - 1];
          // Remove .pptx extension if present
          deckName = fullFilename.replace(/\.pptx?$/i, '') || 'Untitled';
          console.log('[LiveTracker] Using filename from URL:', deckName);
        } else {
          // File not saved yet - try to get a better name
          // presentation.title is the document metadata "Title" property, not the filename
          // It often contains unhelpful values like "Title property in win32"
          // For unsaved files, just use "Untitled Presentation"
          const title = presentation.title;
          if (title && !title.toLowerCase().includes('property') && title !== 'Untitled') {
            deckName = title;
            console.log('[LiveTracker] Using presentation title:', deckName);
          } else {
            deckName = 'Untitled Presentation';
            console.log('[LiveTracker] File not saved, using default name');
          }
        }

        // Get all slide IDs
        slides.items.forEach(slide => {
          slide.load('id');
        });
        await context.sync();
        
        slideIds = slides.items.map(slide => parseSlideId(slide.id));
        console.log('[LiveTracker] Presentation loaded:', {
          name: deckName,
          source: documentUrl || 'unsaved',
          totalSlides: slideIds.length,
          slideIds: slideIds.slice(0, 5)
        });
      });

      // Check if we already have a state for this deck
      console.log(`[LiveTracker] Checking for existing deck state...`);
      try {
        const stats = await this.storage.getStats();
        const trackedDecks = await this.storage.getAllTrackedDecks();
        console.log('[LiveTracker] Storage diagnostics before deck-state check:', {
          documentId,
          trackedDeckCount: trackedDecks.length,
          trackeddocumentIds: trackedDecks.map(d => d.documentId),
          snapshotCount: stats.snapshotCount,
          deckStateCount: stats.deckCount,
        });
      } catch (diagError) {
        console.warn('[LiveTracker] Could not gather storage diagnostics:', diagError);
      }
      const existingState = await this.storage.getDeckState(documentId);
      const isSingleSlideDeck = slideIds.length <= SINGLE_SLIDE_INDEXING_THRESHOLD;
      
      if (!existingState) {
        if (isSingleSlideDeck) {
          console.log(
            `[LiveTracker] 💤 Deferring indexing for single-slide deck ${documentId} (will bootstrap when more slides are added)`
          );
          await this.upsertDeckCatalogEntry({
            documentId,
            deckName,
            slideCount: slideIds.length,
            status: 'deferred',
          });
          await this.storage.saveDeckState({
            documentId: documentId,
            deckName,
            slideIds,
            lastStructureCheck: Date.now(),
            isTracking: true,
            lastSelectionTime: Date.now(),
            indexingDeferred: true,
          });
        } else {
          // First time tracking this deck - do full initialization
          console.log(`[LiveTracker] 🆕 First-time tracking for deck ${documentId} - processing all ${slideIds.length} slides`);
          await this.upsertDeckCatalogEntry({
            documentId,
            deckName,
            slideCount: slideIds.length,
            status: 'opening',
          });
          await this.fullDeckInitialization(documentId, deckName, slideIds);
        }
      } else {
        // Existing deck - check for structural changes
        console.log(`[LiveTracker] ♻️ Resuming tracking for deck ${documentId}`);
        console.log(`[LiveTracker] Previous slide count: ${existingState.slideIds.length}, Current: ${slideIds.length}`);
        const stateAfterStructural = await this.checkStructuralChanges(documentId, slideIds, existingState);

        await this.storage.saveDeckState({
          ...stateAfterStructural,
          documentId: documentId,
          isTracking: true,
          indexingDeferred: isSingleSlideDeck,
        });
        console.log(`[LiveTracker] ✅ Deck state updated`);

        if (isSingleSlideDeck) {
          console.log(
            `[LiveTracker] ⏭️ Skipping resume backfill/reconciliation for single-slide deck ${documentId}`
          );
          return;
        }

        // Keep startup non-blocking: run retry/backfill/reconciliation in the background.
        void this.runResumeBackgroundTasks(documentId, slideIds);
      }
    } catch (error) {
      console.error('[LiveTracker] ❌ Error initializing current deck:', error);
      console.error('[LiveTracker] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error;
    }
  }

  private async upsertDeckCatalogEntry(params: {
    documentId: string;
    deckName: string;
    slideCount: number;
    status: 'opening' | 'deferred' | 'processing' | 'partial' | 'complete' | 'error';
  }): Promise<void> {
    try {
      const response = await this.backendClient.upsertPresentationCatalogEntry({
        documentId: params.documentId,
        deckTitle: params.deckName,
        slideCount: params.slideCount,
        status: params.status,
      });
      console.log('[LiveTracker] ✅ Catalog upsert successful:', {
        documentId: response.deck_id,
        title: response.title,
        slideCount: response.slide_count,
        status: response.status,
      });
    } catch (error) {
      console.warn('[LiveTracker] ⚠️ Catalog upsert failed (continuing local tracking):', {
        documentId: params.documentId,
        status: params.status,
        error,
      });
    }
  }

  /**
   * Full deck initialization - capture all slides
   */
  private async fullDeckInitialization(
    documentId: string,
    deckName: string,
    slideIds: string[]
  ): Promise<void> {
    console.log('[LiveTracker] fullDeckInitialization:', {
      documentId,
      deckName,
      totalSlides: slideIds.length
    });
    
    try {
      await PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load('items');
        await context.sync();
        const snapshots: SlideTrackingSnapshot[] = [];

        // Capture each slide (Office.js part - must be sequential)
        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          slide.load('id,layout');
          await context.sync();

          try {
            // Parse slide ID: Office.js returns "257#0" format, we only want "257"
            const rawSlideId = slide.id;
            const cleanSlideId = parseSlideId(rawSlideId);
            
            // Extract layout info
            let layoutId: string | undefined;
            let layoutName: string | undefined;
            try {
              const layout = slide.layout;
              layout.load('id,name');
              await context.sync();
              layoutId = parseLayoutId(layout.id);  // Normalize with layout_ prefix
              layoutName = layout.name;
            } catch (e) {
              console.warn(`[LiveTracker] Could not get layout info for slide ${i + 1}:`, e);
            }

            // Extract master info
            let masterTemplateId: string | undefined;
            let masterTemplateName: string | undefined;
            try {
              const masterInfo = await getMasterInfoInContext(context, slide);
              if (masterInfo) {
                masterTemplateId = masterId(masterInfo.masterId);  // Normalize with master_ prefix
                masterTemplateName = masterInfo.masterName;
              }
            } catch (e) {
              console.warn(`[LiveTracker] Could not get master info for slide ${i + 1}:`, e);
            }
            
            // Export slide image (correct method - no parameters, then sync)
            const imageResult = slide.getImageAsBase64();
            await context.sync();
            const imageBase64 = imageResult.value || '';
            
            // Export slide PPTX (correct method - no parameters, then sync)
            let pptxBase64 = '';
            try {
              const pptxResult = slide.exportAsBase64();
              await context.sync();
              pptxBase64 = pptxResult.value || '';
            } catch (e) {
              console.warn(`[LiveTracker] ⚠️ Could not export slide ${i + 1} as PPTX:`, e);
            }

            snapshots.push({
              documentId: documentId,
              slideId: cleanSlideId,
              slideIndex: i,
              imageBase64,
              pptxBase64,
              layoutId,
              layoutName,
              masterTemplateId,
              masterTemplateName,
              timestamp: Date.now(),
              touchCount: 0,
              indexingStatus: 'pending',
              // quickMetadata will be added after batch extraction
            });

            console.log(`[LiveTracker] 📸 Captured slide ${i + 1}/${slides.items.length}:`, {
              slideIndex: i + 1,
              rawId: rawSlideId,
              cleanId: cleanSlideId,
              layout: layoutName ? { name: layoutName, id: layoutId } : undefined,
              master: masterTemplateName ? { name: masterTemplateName, id: masterTemplateId } : undefined,
              imageSizeKB: Math.round(imageBase64.length / 1024),
              pptxSizeKB: pptxBase64 ? Math.round(pptxBase64.length / 1024) : 0
            });
          } catch (error) {
            console.error(`[LiveTracker] ❌ Error capturing slide ${i + 1}:`, error);
          }
        }

        // NEW: Batch extract quick metadata for all slides in parallel (single HTTP request)
        console.log(`[LiveTracker] Extracting quick metadata for ${snapshots.length} slides...`);
        const slidesForBatch = snapshots
          .filter((s): s is typeof s & { pptxBase64: string } => !!s.pptxBase64)  // Type guard: only slides with PPTX data
          .map(s => ({ slideId: s.slideId, pptxBase64: s.pptxBase64 }));
        
        if (slidesForBatch.length > 0) {
          try {
            const metadataMap = await this.slideCapture.extractQuickMetadataBatch(slidesForBatch);
            
            // Add metadata to snapshots
            for (const snapshot of snapshots) {
              const metadata = metadataMap.get(snapshot.slideId);
              if (metadata) {
                snapshot.quickMetadata = metadata;
                console.log(`[LiveTracker] ✅ Slide ${snapshot.slideIndex + 1}: "${metadata.slideTitle || '(no title)'}"`);
              }
            }
            
            console.log(`[LiveTracker] ✅ Extracted metadata for ${metadataMap.size}/${slidesForBatch.length} slides`);
          } catch (error) {
            console.warn(`[LiveTracker] Could not extract batch metadata:`, error);
            // Continue without metadata - not critical for initial capture
          }
        }

        // Batch save snapshots (now with quickMetadata)
        await this.storage.batchSaveSnapshots(snapshots);

        // Save deck state
        await this.storage.saveDeckState({
          documentId: documentId,
          deckName,
          slideIds,
          lastStructureCheck: Date.now(),
          isTracking: true,
          lastSelectionTime: Date.now(),
          indexingDeferred: false,
        });

        // Send all slides for initial processing (non-blocking - don't fail if backend is down)
        try {
          await this.queueSlidesForProcessing(documentId, snapshots, 'startup');
        } catch (backendError) {
          console.warn('[LiveTracker] ⚠️ Failed to send slides to backend (will retry on changes):', backendError);
          // Don't throw - local tracking still works, slides will be processed on next change
        }

        console.log('[LiveTracker] ✅ Full deck initialization complete:', {
          documentId,
          deckName,
          snapshotsCaptured: snapshots.length,
          slidesProcessed: snapshots.length
        });
      });
    } catch (error) {
      console.error(`[LiveTracker] ❌ Error in fullDeckInitialization:`, error);
      throw error;
    }
  }

  /**
   * Check for structural changes (added/deleted/reordered slides)
   */
  private async checkStructuralChanges(
    documentId: string,
    currentSlideIds: string[],
    state: DeckTrackingState
  ): Promise<DeckTrackingState> {
    const previousSlideIds = state.slideIds;
    const wasDeferredSingleSlide =
      !!state.indexingDeferred || previousSlideIds.length <= SINGLE_SLIDE_INDEXING_THRESHOLD;
    const isNowMultiSlide = currentSlideIds.length > SINGLE_SLIDE_INDEXING_THRESHOLD;
    const transitionedToMultiSlide = wasDeferredSingleSlide && isNowMultiSlide;

    // Find added slides
    const addedSlideIds = currentSlideIds.filter(id => !previousSlideIds.includes(id));
    
    // Find deleted slides
    const deletedSlideIds = previousSlideIds.filter(id => !currentSlideIds.includes(id));

    if (transitionedToMultiSlide) {
      console.log(
        `[LiveTracker] 🚀 Single-slide deck transitioned to multi-slide (${previousSlideIds.length} -> ${currentSlideIds.length}); bootstrapping missing snapshots`
      );
      await this.bootstrapMissingSnapshotsForDeck(documentId, currentSlideIds);
    } else if (addedSlideIds.length > 0) {
      console.log(`➕ Detected ${addedSlideIds.length} new slides`);
      await this.handleAddedSlides(documentId, addedSlideIds);
    }

    if (deletedSlideIds.length > 0) {
      console.log(`➖ Detected ${deletedSlideIds.length} deleted slides`);
      await this.handleDeletedSlides(documentId, deletedSlideIds);
    }

    // Check for reordering (same slides, different order)
    const reordered = this.detectReordering(previousSlideIds, currentSlideIds);
    if (reordered) {
      console.log(`🔄 Detected slide reordering`);
      await this.handleSlideReordering(documentId, currentSlideIds);
    }

    const updatedState: DeckTrackingState = {
      ...state,
      slideIds: currentSlideIds,
      lastStructureCheck: Date.now(),
      indexingDeferred: currentSlideIds.length <= SINGLE_SLIDE_INDEXING_THRESHOLD,
    };

    // Update state with current slide IDs
    await this.storage.saveDeckState(updatedState);
    return updatedState;
  }

  /**
   * On resume, immediately retry slides that previously failed classification.
   * This keeps reopen behavior deterministic without forcing full-deck reprocessing.
   */
  private async retryFailedSlidesOnResume(documentId: string): Promise<void> {
    try {
      const snapshots = await this.storage.getDeckSnapshots(documentId);
      const failedSnapshots = snapshots.filter(snapshot =>
        isFailedDescription(snapshot.shortDescription)
      );

      if (failedSnapshots.length === 0) {
        console.log(`[LiveTracker] No failed slides to retry for deck ${documentId}`);
        return;
      }

      console.log(`[LiveTracker] Retrying ${failedSnapshots.length} failed slides on resume`, {
        documentId,
        slideIds: failedSnapshots.map(s => s.slideId),
      });

      await this.queueSlidesForProcessing(documentId, failedSnapshots, 'startup');
    } catch (error) {
      console.warn('[LiveTracker] Failed to queue retry for failed slides on resume:', error);
    }
  }

  /**
   * On resume, queue slides that have never received a usable description.
   */
  private async requeueUnindexedSlidesOnResume(documentId: string): Promise<void> {
    try {
      const snapshots = await this.storage.getDeckSnapshots(documentId);
      const unindexedSnapshots = snapshots.filter(snapshot => {
        if (isFailedDescription(snapshot.shortDescription)) {
          return false; // handled by retryFailedSlidesOnResume
        }
        return needsBackfillDescription(snapshot.shortDescription);
      });

      if (unindexedSnapshots.length === 0) {
        console.log(`[LiveTracker] No unindexed slides to backfill for deck ${documentId}`);
        return;
      }

      console.log(`[LiveTracker] Re-queuing ${unindexedSnapshots.length} unindexed slides on resume`, {
        documentId,
        slideIds: unindexedSnapshots.map(s => s.slideId),
      });
      await this.queueSlidesForProcessing(documentId, unindexedSnapshots, 'startup');
    } catch (error) {
      console.warn('[LiveTracker] Failed to queue unindexed slides on resume:', error);
    }
  }

  /**
   * Run resume tasks in the background so startup is not blocked.
   */
  private async runResumeBackgroundTasks(documentId: string, currentSlideIds: string[]): Promise<void> {
    const startedAt = Date.now();
    console.log('[LiveTracker] Starting background resume tasks...', {
      documentId,
      totalSlides: currentSlideIds.length,
    });

    try {
      await this.retryFailedSlidesOnResume(documentId);
      await this.requeueUnindexedSlidesOnResume(documentId);
      await this.reconcileDeckContentInBackground(documentId, currentSlideIds);
      console.log('[LiveTracker] ✅ Background resume tasks complete:', {
        documentId,
        elapsedMs: Date.now() - startedAt,
      });
    } catch (error) {
      console.warn('[LiveTracker] Background resume tasks failed:', error);
    }
  }

  /**
   * Bootstrap any slides that are missing local snapshots.
   * Used when a deck transitions from deferred single-slide mode to multi-slide.
   */
  private async bootstrapMissingSnapshotsForDeck(documentId: string, currentSlideIds: string[]): Promise<void> {
    const existingSnapshots = await this.storage.getDeckSnapshots(documentId);
    const existingSlideIds = new Set(existingSnapshots.map((snapshot) => snapshot.slideId));
    const missingSlideIds = currentSlideIds.filter((slideId) => !existingSlideIds.has(slideId));

    if (missingSlideIds.length === 0) {
      console.log(`[LiveTracker] No missing snapshots to bootstrap for deck ${documentId}`);
      return;
    }

    console.log('[LiveTracker] Bootstrapping missing snapshots for transitioned deck:', {
      documentId,
      missingCount: missingSlideIds.length,
      slideIds: missingSlideIds,
    });

    const snapshots = await this.captureSnapshotsForSlideIds(documentId, new Set(missingSlideIds));
    if (snapshots.length === 0) {
      console.warn('[LiveTracker] Missing snapshot bootstrap captured no slides');
      return;
    }

    await this.storage.batchSaveSnapshots(snapshots);
    await this.queueSlidesForProcessingInChunks(
      documentId,
      snapshots,
      'startup',
      RECONCILIATION_QUEUE_BATCH_SIZE
    );
  }

  /**
   * Capture snapshots for specific slide IDs.
   */
  private async captureSnapshotsForSlideIds(
    documentId: string,
    targetSlideIds: Set<string>
  ): Promise<SlideTrackingSnapshot[]> {
    const snapshots: SlideTrackingSnapshot[] = [];

    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        slide.load('id,layout');
        await context.sync();

        const cleanSlideId = parseSlideId(slide.id);
        if (!targetSlideIds.has(cleanSlideId)) {
          continue;
        }

        try {
          let resolvedLayoutId: string | undefined;
          let layoutName: string | undefined;
          try {
            const layout = slide.layout;
            layout.load('id,name');
            await context.sync();
            resolvedLayoutId = parseLayoutId(layout.id);
            layoutName = layout.name;
          } catch (error) {
            console.warn(`[LiveTracker] Could not get layout info for slide ${cleanSlideId}:`, error);
          }

          let masterTemplateId: string | undefined;
          let masterTemplateName: string | undefined;
          try {
            const masterInfo = await getMasterInfoInContext(context, slide);
            if (masterInfo) {
              masterTemplateId = masterId(masterInfo.masterId);
              masterTemplateName = masterInfo.masterName;
            }
          } catch (error) {
            console.warn(`[LiveTracker] Could not get master info for slide ${cleanSlideId}:`, error);
          }

          const imageResult = slide.getImageAsBase64();
          await context.sync();
          const imageBase64 = imageResult.value || '';

          let pptxBase64 = '';
          try {
            const pptxResult = slide.exportAsBase64();
            await context.sync();
            pptxBase64 = pptxResult.value || '';
          } catch (error) {
            console.warn(`[LiveTracker] Could not export slide ${cleanSlideId} as PPTX:`, error);
          }

          snapshots.push({
            documentId: documentId,
            slideId: cleanSlideId,
            slideIndex: i,
            imageBase64,
            pptxBase64,
            layoutId: resolvedLayoutId,
            layoutName,
            masterTemplateId,
            masterTemplateName,
            timestamp: Date.now(),
            touchCount: 0,
            indexingStatus: 'pending',
          });
        } catch (error) {
          console.error(`[LiveTracker] Error capturing snapshot for ${cleanSlideId}:`, error);
        }
      }
    });

    return snapshots;
  }

  /**
   * Reconcile a deck by checking all slides for changes without blocking startup.
   * Queues only changed or missing slides.
   */
  private async reconcileDeckContentInBackground(documentId: string, currentSlideIds: string[]): Promise<void> {
    const currentSlideSet = new Set(currentSlideIds);
    const existingSnapshots = await this.storage.getDeckSnapshots(documentId);
    const existingBySlideId = new Map(existingSnapshots.map((snapshot) => [snapshot.slideId, snapshot]));
    const changedOrMissingSnapshots: SlideTrackingSnapshot[] = [];

    console.log('[LiveTracker] Starting background reconciliation sweep...', {
      documentId,
      totalSlides: currentSlideIds.length,
    });

    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        slide.load('id,layout');
        await context.sync();

        const cleanSlideId = parseSlideId(slide.id);
        if (!currentSlideSet.has(cleanSlideId)) {
          continue;
        }

        const existingSnapshot = existingBySlideId.get(cleanSlideId);
        const imageResult = slide.getImageAsBase64();
        await context.sync();
        const imageBase64 = imageResult.value || '';

        const isMissingSnapshot = !existingSnapshot;
        const hasChanged = !!existingSnapshot && existingSnapshot.imageBase64 !== imageBase64;
        if (!isMissingSnapshot && !hasChanged) {
          continue;
        }

        let resolvedLayoutId: string | undefined;
        let layoutName: string | undefined;
        try {
          const layout = slide.layout;
          layout.load('id,name');
          await context.sync();
          resolvedLayoutId = parseLayoutId(layout.id);
          layoutName = layout.name;
        } catch (error) {
          console.warn(`[LiveTracker] Could not get layout info during reconciliation for ${cleanSlideId}:`, error);
        }

        let masterTemplateId: string | undefined;
        let masterTemplateName: string | undefined;
        try {
          const masterInfo = await getMasterInfoInContext(context, slide);
          if (masterInfo) {
            masterTemplateId = masterId(masterInfo.masterId);
            masterTemplateName = masterInfo.masterName;
          }
        } catch (error) {
          console.warn(`[LiveTracker] Could not get master info during reconciliation for ${cleanSlideId}:`, error);
        }

        let pptxBase64 = existingSnapshot?.pptxBase64 || '';
        try {
          const pptxResult = slide.exportAsBase64();
          await context.sync();
          pptxBase64 = pptxResult.value || '';
        } catch (error) {
          console.warn(`[LiveTracker] Could not export slide during reconciliation for ${cleanSlideId}:`, error);
        }

        changedOrMissingSnapshots.push({
          documentId: documentId,
          slideId: cleanSlideId,
          slideIndex: i,
          imageBase64,
          pptxBase64,
          prevImageBase64: hasChanged ? existingSnapshot?.imageBase64 : existingSnapshot?.prevImageBase64,
          prevPptxBase64: hasChanged ? existingSnapshot?.pptxBase64 : existingSnapshot?.prevPptxBase64,
          prevTimestamp: hasChanged ? existingSnapshot?.timestamp : existingSnapshot?.prevTimestamp,
          layoutId: resolvedLayoutId || existingSnapshot?.layoutId,
          layoutName: layoutName || existingSnapshot?.layoutName,
          masterTemplateId: masterTemplateId || existingSnapshot?.masterTemplateId,
          masterTemplateName: masterTemplateName || existingSnapshot?.masterTemplateName,
          timestamp: Date.now(),
          lastProcessed: existingSnapshot?.lastProcessed,
          touchCount: existingSnapshot?.touchCount || 0,
          shortDescription: existingSnapshot?.shortDescription,
          descriptionUpdatedAt: existingSnapshot?.descriptionUpdatedAt,
          quickMetadata: existingSnapshot?.quickMetadata,
          indexingStatus: isMissingSnapshot ? 'pending' : existingSnapshot?.indexingStatus || 'pending',
        });

        console.log('[LiveTracker] Reconciliation detected slide requiring reindex:', {
          slideId: cleanSlideId,
          slideIndex: i + 1,
          reason: isMissingSnapshot ? 'missing_snapshot' : 'image_changed',
        });
      }
    });

    if (changedOrMissingSnapshots.length === 0) {
      console.log(`[LiveTracker] Reconciliation found no changed/missing slides for ${documentId}`);
      return;
    }

    await this.storage.batchSaveSnapshots(changedOrMissingSnapshots);
    await this.queueSlidesForProcessingInChunks(
      documentId,
      changedOrMissingSnapshots,
      'startup',
      RECONCILIATION_QUEUE_BATCH_SIZE
    );
  }

  private async queueSlidesForProcessingInChunks(
    documentId: string,
    snapshots: SlideTrackingSnapshot[],
    trigger: 'startup' | 'bulk_add' | 'single',
    chunkSize: number
  ): Promise<void> {
    if (snapshots.length === 0) {
      return;
    }

    const safeChunkSize = Math.max(1, chunkSize);
    for (let i = 0; i < snapshots.length; i += safeChunkSize) {
      const chunk = snapshots.slice(i, i + safeChunkSize);
      await this.queueSlidesForProcessing(documentId, chunk, trigger);
      if (i + safeChunkSize < snapshots.length) {
        await this.waitForThrottle(40);
      }
    }
  }

  private async waitForThrottle(ms: number): Promise<void> {
    await new Promise<void>((resolve) => window.setTimeout(resolve, ms));
  }

  private buildQueueFingerprint(
    documentId: string,
    trigger: 'startup' | 'bulk_add' | 'single',
    snapshots: SlideTrackingSnapshot[]
  ): string {
    const uniqueSortedSlideIds = [...new Set(snapshots.map((snapshot) => snapshot.slideId))].sort();
    return `${documentId}::${trigger}::${uniqueSortedSlideIds.join('|')}`;
  }

  private pruneRecentQueueFingerprints(now: number): void {
    for (const [fingerprint, timestamp] of this.recentQueueFingerprints.entries()) {
      if (now - timestamp > QUEUE_DEDUPE_WINDOW_MS) {
        this.recentQueueFingerprints.delete(fingerprint);
      }
    }
  }

  /**
   * Detect if slides were reordered (same slides, different order)
   */
  private detectReordering(previousIds: string[], currentIds: string[]): boolean {
    // If different lengths, it's not just reordering
    if (previousIds.length !== currentIds.length) {
      return false;
    }

    // Check if same slides exist
    const previousSet = new Set(previousIds);
    const currentSet = new Set(currentIds);
    
    if (previousSet.size !== currentSet.size) {
      return false;
    }

    for (const id of currentIds) {
      if (!previousSet.has(id)) {
        return false;
      }
    }

    // Same slides - check if order changed
    for (let i = 0; i < previousIds.length; i++) {
      if (previousIds[i] !== currentIds[i]) {
        return true;  // Order changed
      }
    }

    return false;  // Same order
  }

  /**
   * Handle slide reordering - sync indexes to backend
   */
  private async handleSlideReordering(documentId: string, currentSlideIds: string[]): Promise<void> {
    try {
      // Build mappings of slideId -> new index
      const mappings = currentSlideIds.map((slideId, index) => ({
        slideId,
        slideIndex: index
      }));

      console.log(`[LiveTracker] Syncing ${mappings.length} slide indexes to backend`);

      // Update backend
      const result = await this.backendClient.updateSlideIndexes(documentId, mappings);
      console.log(`[LiveTracker] ✅ Updated ${result.updated_count}/${result.total_requested} slide indexes`);

      if (result.errors && result.errors.length > 0) {
        console.warn(`[LiveTracker] ⚠️ Some index updates failed:`, result.errors);
      }

      // Also update local snapshots with new indexes
      for (const mapping of mappings) {
        const snapshot = await this.storage.getSnapshot(documentId, mapping.slideId);
        if (snapshot && snapshot.slideIndex !== mapping.slideIndex) {
          await this.storage.saveSnapshot({
            ...snapshot,
            slideIndex: mapping.slideIndex
          });
        }
      }

      // Notify handlers
      this.notifyChangeDetection({
        type: 'structural_check',
        documentId,
        slideIds: currentSlideIds,
        slideIndexes: currentSlideIds.map((_, i) => i),
        timestamp: Date.now(),
        details: `Slides reordered - ${mappings.length} indexes updated`
      });

    } catch (error) {
      console.error('[LiveTracker] ❌ Failed to sync slide indexes:', error);
    }
  }

  /**
   * Handle added slides
   */
  private async handleAddedSlides(documentId: string, addedSlideIds: string[]): Promise<void> {
    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      const snapshots: SlideTrackingSnapshot[] = [];
      const slideIndexes: number[] = [];

      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        slide.load('id,layout');
        await context.sync();

        const rawSlideId = slide.id;
        const cleanSlideId = parseSlideId(rawSlideId);
        
        if (addedSlideIds.includes(cleanSlideId)) {
          slideIndexes.push(i);

          try {
            // Extract layout info
            let layoutId: string | undefined;
            let layoutName: string | undefined;
            try {
              const layout = slide.layout;
              layout.load('id,name');
              await context.sync();
              layoutId = parseLayoutId(layout.id);  // Normalize with layout_ prefix
              layoutName = layout.name;
            } catch (e) {
              console.warn(`[LiveTracker] Could not get layout info for new slide ${cleanSlideId}:`, e);
            }

            // Extract master info
            let masterTemplateId: string | undefined;
            let masterTemplateName: string | undefined;
            try {
              const masterInfo = await getMasterInfoInContext(context, slide);
              if (masterInfo) {
                masterTemplateId = masterId(masterInfo.masterId);  // Normalize with master_ prefix
                masterTemplateName = masterInfo.masterName;
              }
            } catch (e) {
              console.warn(`[LiveTracker] Could not get master info for new slide ${cleanSlideId}:`, e);
            }

            // Correct export method
            const imageResult = slide.getImageAsBase64();
            await context.sync();
            const imageBase64 = imageResult.value || '';
            
            let pptxBase64 = '';
            try {
              const pptxResult = slide.exportAsBase64();
              await context.sync();
              pptxBase64 = pptxResult.value || '';
            } catch (e) {
              console.warn(`[LiveTracker] Could not export new slide ${cleanSlideId} as PPTX:`, e);
            }

            snapshots.push({
              documentId: documentId,
              slideId: cleanSlideId,
              slideIndex: i,
              imageBase64,
              pptxBase64,
              layoutId,
              layoutName,
              masterTemplateId,
              masterTemplateName,
              timestamp: Date.now(),
              touchCount: 0,
              indexingStatus: 'pending'
            });
          } catch (error) {
            console.error(`Error capturing new slide ${cleanSlideId}:`, error);
          }
        }
      }

      if (snapshots.length > 0) {
        await this.storage.batchSaveSnapshots(snapshots);
        await this.queueSlidesForProcessing(documentId, snapshots, 'bulk_add');

        // Notify handlers
        this.notifyChangeDetection({
          type: 'slides_added',
          documentId,
          slideIds: addedSlideIds,
          slideIndexes,
          timestamp: Date.now(),
          details: `${addedSlideIds.length} new slides`
        });

        this.changesDetected += addedSlideIds.length;
      }
    });
  }

  /**
   * Handle deleted slides
   */
  private async handleDeletedSlides(documentId: string, deletedSlideIds: string[]): Promise<void> {
    // Remove snapshots for deleted slides (local IndexedDB)
    // Note: shortDescription is stored in the snapshot, so deleting snapshot removes it
    for (const slideId of deletedSlideIds) {
      await this.storage.deleteSnapshot(documentId, slideId);
    }

    // Notify backend to mark slides as unavailable (sets is_available=false)
    try {
      const result = await this.backendClient.markSlidesUnavailable(documentId, deletedSlideIds);
      console.log(`[LiveTracker] ✅ Marked ${result.marked_unavailable} slides as unavailable in backend`);
      if (result.errors && result.errors.length > 0) {
        console.warn(`[LiveTracker] ⚠️ Some slides failed to mark:`, result.errors);
      }
    } catch (error) {
      console.warn(`[LiveTracker] ⚠️ Failed to mark slides unavailable in backend:`, error);
      // Don't throw - local tracking still works
    }

    // Notify handlers
    this.notifyChangeDetection({
      type: 'slides_deleted',
      documentId,
      slideIds: deletedSlideIds,
      slideIndexes: [], // Don't know indexes of deleted slides
      timestamp: Date.now(),
      details: `${deletedSlideIds.length} slides deleted`
    });
  }

  /**
   * Handle selection changed event
   */
  private async onSelectionChanged(_eventArgs: any): Promise<void> {
    if (!this.isEnabled) return;

    const now = Date.now();
    const timeSinceLastSelection = now - this.lastSelectionTime;

    try {
      await PowerPoint.run(async (context) => {
        const documentId = await getOrCreatedocumentId();
        const selectedSlides = context.presentation.getSelectedSlides();
        selectedSlides.load('items/id,items/layout');
        await context.sync();

        if (selectedSlides.items.length === 0) return;

        const currentSlideItem = selectedSlides.items[0];
        currentSlideItem.load('id,layout');
        await context.sync();

        const currentSlide = {
          documentId,
          slideId: parseSlideId(currentSlideItem.id)
        };

        // Get layout info for active layout tracking
        let layoutId: string | undefined;
        try {
          const layout = currentSlideItem.layout;
          layout.load('id');
          await context.sync();
          layoutId = parseLayoutId(layout.id);  // Normalize with layout_ prefix
        } catch (e) {
          console.warn('[LiveTracker] Could not get layout for current slide');
        }

        // Update active layout state (non-blocking)
        if (layoutId) {
          this.updateActiveLayoutState(documentId, layoutId, currentSlide.slideId);
        }

        // Check if we switched to a different presentation
        if (this.lastSelectedSlide && this.lastSelectedSlide.documentId !== currentSlide.documentId) {
          console.log('[LiveTracker] 🔄 Deck switch detected:', {
            from: this.lastSelectedSlide.documentId,
            to: currentSlide.documentId
          });
          // Clear active presentation state (descriptions persist in IndexedDB per-deck)
          this.activePresentation = null;
          // Re-initialize tracking for the new deck
          await this.initializeCurrentDeck();
          // Update last selection and return - don't process as normal navigation
          this.lastSelectedSlide = currentSlide;
          this.lastSelectionTime = now;
          return;
        }

        // Check if we navigated away from a different slide
        if (this.lastSelectedSlide && 
            this.lastSelectedSlide.slideId !== currentSlide.slideId &&
            this.lastSelectedSlide.documentId === currentSlide.documentId) {
          
          // Check if rapid navigation (< 1 second)
          if (timeSinceLastSelection < this.rapidNavThreshold) {
            console.log('[LiveTracker] ⚡ Rapid navigation detected - only structural check');
            // For rapid navigation, only do bulk check (structural changes)
            await this.quickStructuralCheck(documentId);
          } else {
            // Normal navigation - check if previous slide changed
            console.log(`[LiveTracker] 🔍 Checking if slide ${this.lastSelectedSlide.slideId} changed (navigated away)...`);
            await this.checkSlideForChanges(
              this.lastSelectedSlide.documentId,
              this.lastSelectedSlide.slideId
            );
            
            // Also do structural check
            await this.quickStructuralCheck(documentId);
          }
        }

        // Update last selection
        this.lastSelectedSlide = currentSlide;
        this.lastSelectionTime = now;
      });
    } catch (error) {
      console.error('Error in selection changed handler:', error);
    }
  }

  /**
   * Update active layout state with current slide info
   * Non-blocking - fetches descriptions asynchronously
   * Uses IndexedDB as the source of truth (no in-memory cache)
   */
  private async updateActiveLayoutState(documentId: string, layoutId: string, slideId: string): Promise<void> {
    // Initialize active presentation state if needed
    if (!this.activePresentation || this.activePresentation.documentId !== documentId) {
      this.activePresentation = {
        documentId,
        layouts: new Map()
      };
    }

    // First check IndexedDB for description
    let shortDescription: string | undefined;
    try {
      const snapshot = await this.storage.getSnapshot(documentId, slideId);
      shortDescription = snapshot?.shortDescription;
    } catch (error) {
      console.warn(`[LiveTracker] Failed to get snapshot from IndexedDB:`, error);
    }

    if (shortDescription) {
      // Use description from IndexedDB
      this.activePresentation.layouts.set(layoutId, {
        slide_id: `slide_${slideId}`,
        shortDescription
      });
      console.log('[LiveTracker] Using IndexedDB description:', {
        layoutId,
        slideId: `slide_${slideId}`,
        shortDescription
      });
    } else {
      // Set loading state immediately
      this.activePresentation.layouts.set(layoutId, {
        slide_id: `slide_${slideId}`,
        shortDescription: 'Loading description...',
        isLoading: true
      });

      // Fetch description from backend (non-blocking) - this also saves to IndexedDB
      this.fetchSlideShortDescriptions(documentId, [slideId]).then(descriptions => {
        if (descriptions.length > 0) {
          const desc = descriptions[0];
          // Update the active layout state with the fetched description
          if (this.activePresentation && this.activePresentation.documentId === documentId) {
            this.activePresentation.layouts.set(layoutId, {
              slide_id: desc.slide_id,
              shortDescription: desc.shortDescription,
              isLoading: false
            });
            console.log(`[LiveTracker] Updated layout ${layoutId}: ${desc.slide_id} - "${desc.shortDescription}"`);
          }
        }
      }).catch(error => {
        console.warn(`[LiveTracker] Failed to fetch description for slide ${slideId}:`, error);
        // Update with error state
        if (this.activePresentation && this.activePresentation.documentId === documentId) {
          this.activePresentation.layouts.set(layoutId, {
            slide_id: `slide_${slideId}`,
            shortDescription: 'Description unavailable',
            isLoading: false
          });
        }
      });
    }
  }

  /**
   * Fetch short descriptions for multiple slides from backend and sync to IndexedDB
   * IndexedDB is the source of truth - no in-memory cache
   */
  private async fetchSlideShortDescriptions(documentId: string, slideIds: string[]): Promise<Array<{
    slide_id: string;
    shortDescription: string;
    is_available: boolean;
  }>> {
    try {
      const result = await this.backendClient.getSlideShortDescriptions(documentId, slideIds);
      
      // Sync to IndexedDB storage (source of truth)
      const descriptionsToSync = result.slides
        .filter(s => s.is_available && s.shortDescription)
        .map(s => ({
          // Normalize ID - remove slide_ prefix if present for storage key matching
          slideId: s.slide_id.replace('slide_', ''),
          shortDescription: s.shortDescription
        }));
      
      if (descriptionsToSync.length > 0) {
        try {
          const updated = await this.storage.batchUpdateDescriptions(documentId, descriptionsToSync);
          if (updated > 0) {
            console.log('[LiveTracker] Synced descriptions to IndexedDB:', {
              updatedCount: updated,
              documentId
            });
          }
        } catch (storageError) {
          console.warn('[LiveTracker] Failed to sync descriptions to IndexedDB:', storageError);
        }
      }
      
      return result.slides;
    } catch (error) {
      console.warn(`[LiveTracker] Failed to fetch descriptions from backend:`, error);
      return slideIds.map(id => ({
        slide_id: `slide_${id}`,
        shortDescription: 'Description unavailable',
        is_available: false
      }));
    }
  }

  /**
   * Get current active presentation layout state
   * Returns a map of layoutId -> { slide_id, shortDescription }
   */
  getActiveLayoutState(): ActivePresentationState | null {
    return this.activePresentation;
  }

  /**
   * Get the short description for a specific slide.
   * Checks IndexedDB first, then backend API
   * IndexedDB is the source of truth - no in-memory cache
   */
  async getSlideShortDescription(documentId: string, slideId: string): Promise<string> {
    // Check IndexedDB storage (source of truth)
    try {
      const snapshot = await this.storage.getSnapshot(documentId, slideId);
      if (snapshot?.shortDescription) {
        return snapshot.shortDescription;
      }
    } catch (storageError) {
      console.warn('[LiveTracker] Failed to check IndexedDB for description:', storageError);
    }

    // Fetch from backend and sync to IndexedDB
    const descriptions = await this.fetchSlideShortDescriptions(documentId, [slideId]);
    if (descriptions.length > 0 && descriptions[0].shortDescription) {
      return descriptions[0].shortDescription;
    }

    return 'No description available';
  }

  /**
   * Clear active layout state
   * Note: Descriptions persist in IndexedDB and are not cleared here
   */
  clearActiveLayoutState(): void {
    this.activePresentation = null;
    console.log('[LiveTracker] Cleared active layout state');
  }

  /**
   * Connect WebSocket for real-time updates from backend
   */
  private connectWebSocket(documentId: string): void {
    console.log(`[LiveTracker] Connecting WebSocket for deck ${documentId}...`);
    
    // Connect via backendClient
    this.backendClient.connectProgressWebSocket(documentId);
    
    // Register handler for progress updates (which includes description updates)
    this.backendClient.onProgress((update) => {
      this.handleWebSocketMessage(update);
    });
    
    console.log(`[LiveTracker] ✅ WebSocket connected for deck ${documentId}`);
  }

  /**
   * Handle incoming WebSocket message
   * Standard format: { connection_type, update_type, deck_id, update_data, timestamp }
   */
  private handleWebSocketMessage(message: any): void {
    if (message.update_type) {
      console.log(`[LiveTracker] 📥 WebSocket update: ${message.update_type}`);

      switch (message.update_type) {
        case WS_UPDATE_TYPES.DESCRIPTIONS_READY:
          void this.handleDescriptionsUpdate(message.deck_id, message.update_data);
          // Refresh deck registry so slidesReady count updates
          this.refreshDeckRegistryAsync(WS_UPDATE_TYPES.DESCRIPTIONS_READY);
          break;
        case WS_UPDATE_TYPES.SLIDE_PROCESSED:
          console.log('[LiveTracker] Slide processed:', {
            slideIds: message.update_data?.slide_ids,
            documentId: message.deck_id
          });
          // Refresh deck registry when slides finish processing
          this.refreshDeckRegistryAsync(WS_UPDATE_TYPES.SLIDE_PROCESSED);
          break;
        case WS_UPDATE_TYPES.PROCESSING_COMPLETE:
        case WS_UPDATE_TYPES.DECK_COMPLETE:
          console.log('[LiveTracker] Processing complete:', {
            documentId: message.deck_id,
            status: message.update_data?.status,
            processed: message.update_data?.slides_processed,
            failed: message.update_data?.slides_failed
          });
          // Refresh deck registry on completion
          this.refreshDeckRegistryAsync(message.update_type);
          break;
        case WS_UPDATE_TYPES.SLIDES_FAILED:
          console.warn('[LiveTracker] Slide-processing failures received:', {
            documentId: message.deck_id,
            batch: message.update_data?.batch,
            failedCount: message.update_data?.failed_slides?.length || 0
          });
          void this.handleSlidesFailed(message.deck_id, message.update_data);
          this.refreshDeckRegistryAsync(WS_UPDATE_TYPES.SLIDES_FAILED);
          break;
        case WS_UPDATE_TYPES.ERROR:
          console.error(`[LiveTracker] Processing error: ${message.update_data?.error}`);
          break;
      }
    }
    
    // Also handle legacy format for backward compatibility
    if (message.type === 'complete' && message.slideId) {
      console.log('[LiveTracker] Legacy complete message:', {
        slideId: message.slideId
      });
      this.refreshDeckRegistryAsync('legacy_complete');
    }
  }

  /**
   * Handle explicit failed-slide updates from backend batch processing.
   * Keep local descriptions in sync so retries and UI status are accurate.
   */
  private async handleSlidesFailed(
    documentId: string,
    data: { failed_slides?: Array<{ slide_id: string; error?: string }> }
  ): Promise<void> {
    const failedSlides = data?.failed_slides || [];
    if (!documentId || failedSlides.length === 0) {
      return;
    }

    for (const failed of failedSlides) {
      const rawId = failed.slide_id || '';
      if (!rawId) {
        continue;
      }

      const normalizedId = rawId.startsWith('slide_') ? rawId : `slide_${rawId}`;
      try {
        await this.storage.updateSlideDescription(documentId, normalizedId, 'Processing failed');
        await this.storage.updateSlideIndexingStatus(documentId, normalizedId, 'failed');
      } catch (error) {
        console.warn(`[LiveTracker] Failed to sync failed slide state for ${normalizedId}:`, error);
      }
    }
  }
  
  /**
   * Refresh deck registry asynchronously (non-blocking)
   * Called when slides finish processing so the registry stays up-to-date
   */
  private refreshDeckRegistryAsync(trigger: string): void {
    console.log(`[LiveTracker] 🔄 Refreshing deck registry (trigger: ${trigger})...`);
    
    getDeckRegistry().refresh().then(() => {
      const registry = getDeckRegistry();
      const allDecks = registry.getAllDecks();
      
      console.log(`[LiveTracker] ✅ Deck registry refreshed after ${trigger}`);
      console.log(`[LiveTracker]    Total decks: ${allDecks.length}`);
      
      for (const deck of allDecks) {
        const statusIcon = deck.status === 'complete' ? '✓' : 
                           deck.status === 'partial' ? '◐' : '⏳';
        console.log(`[LiveTracker]    ${deck.isCurrent ? '→ ' : '  '}"${deck.title}" ${deck.slidesReady}/${deck.totalSlides} ${statusIcon}`);
      }
    }).catch(error => {
      console.warn('[LiveTracker] ⚠️ Failed to refresh deck registry:', error);
    });
  }

  /**
   * Handle descriptions update from WebSocket push
   * Updates IndexedDB (source of truth) and active layout state
   */
  private async handleDescriptionsUpdate(
    documentId: string,
    data: { slide_ids?: string[]; descriptions?: Array<{ slide_id: string; short_description: string }> }
  ): Promise<void> {
    if (!data?.descriptions?.length) {
      console.log('[LiveTracker] No descriptions in update');
      return;
    }

    console.log('[LiveTracker] 📝 Updating descriptions from WebSocket:', {
      count: data.descriptions.length,
      documentId
    });

    for (const desc of data.descriptions) {
      // Normalize slide ID: IndexedDB stores with 'slide_' prefix
      // Backend may send with or without prefix, so ensure it has the prefix
      const normalizedId = desc.slide_id.startsWith('slide_') 
        ? desc.slide_id 
        : `slide_${desc.slide_id}`;
      
      // Update IndexedDB (source of truth)
      try {
        await this.storage.updateSlideDescription(documentId, normalizedId, desc.short_description);
        const status: SlideIndexingStatus = isFailedDescription(desc.short_description) ? 'failed' : 'success';
        await this.storage.updateSlideIndexingStatus(documentId, normalizedId, status);
      } catch (error) {
        console.warn(`[LiveTracker] Failed to update description in IndexedDB for ${normalizedId}:`, error);
      }
      
      // Update active presentation state if it's the current deck
      if (this.activePresentation?.documentId === documentId) {
        // Find and update any layout entries for this slide
        for (const [layoutId, entry] of this.activePresentation.layouts.entries()) {
          if (entry.slide_id === normalizedId || entry.slide_id === desc.slide_id) {
            this.activePresentation.layouts.set(layoutId, {
              ...entry,
              shortDescription: desc.short_description,
              isLoading: false
            });
            console.log(`[LiveTracker] Updated layout ${layoutId} with new description`);
          }
        }
      }
    }

    console.log('[LiveTracker] ✅ Synced descriptions to IndexedDB:', {
      count: data.descriptions.length,
      documentId
    });
  }

  /**
   * Quick structural check (slide count)
   */
  private async quickStructuralCheck(documentId: string): Promise<void> {
    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items/id');
      await context.sync();

      const currentSlideIds = slides.items.map(s => parseSlideId(s.id));
      const state = await this.storage.getDeckState(documentId);

      if (state && state.slideIds.length !== currentSlideIds.length) {
        console.log('📊 Structural change detected:', {
          previousCount: state.slideIds.length,
          currentCount: currentSlideIds.length,
          documentId
        });
        await this.checkStructuralChanges(documentId, currentSlideIds, state);
      }
    });
  }

  /**
   * Check if a specific slide has changed
   * Uses SlideCapture utility for capture and comparison
   */
  private async checkSlideForChanges(documentId: string, slideId: string): Promise<void> {
    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();

      // Find the slide (slideId is already cleaned, but Office.js returns raw format)
      let targetSlide: PowerPoint.Slide | null = null;
      let slideIndex = -1;
      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        slide.load('id');
        await context.sync();
        const cleanRawId = parseSlideId(slide.id);
        if (cleanRawId === slideId) {
          targetSlide = slide;
          slideIndex = i;
          break;
        }
      }

      if (!targetSlide) {
        console.log(`[LiveTracker] Slide ${slideId} not found - may have been deleted`);
        return;
      }

      // Use SlideCapture utility for capture and comparison
      const result = await this.slideCapture.captureAndSync(context, targetSlide, slideIndex, documentId);

      if (result.hasChanged || result.isNew) {
        console.log(`[LiveTracker] ✏️ ${result.isNew ? 'New' : 'Changed'} slide ${slideIndex + 1}`);

        // Queue for reprocessing
        await this.queueSlidesForProcessing(documentId, [result.snapshot], 'single');

        // Notify handlers
        this.notifyChangeDetection({
          type: 'content_change',
          documentId,
          slideIds: [slideId],
          slideIndexes: [slideIndex],
          timestamp: Date.now(),
          details: `Slide ${slideIndex + 1} ${result.isNew ? 'captured' : 'edited'}`
        });

        this.changesDetected++;
      } else {
        console.log(`[LiveTracker] ✓ No changes detected in slide ${slideIndex + 1}`);
      }
    });
  }

  /**
   * Queue slides for processing
   */
  private async queueSlidesForProcessing(
    documentId: string,
    snapshots: SlideTrackingSnapshot[],
    trigger: 'startup' | 'bulk_add' | 'single'
  ): Promise<void> {
    if (snapshots.length === 0) {
      return;
    }

    const fingerprint = this.buildQueueFingerprint(documentId, trigger, snapshots);
    const now = Date.now();
    this.pruneRecentQueueFingerprints(now);

    const inFlightMatch = this.inFlightQueueFingerprints.get(fingerprint);
    if (inFlightMatch) {
      console.log('[LiveTracker] 🔁 Deduped queue request (in flight):', {
        documentId,
        trigger,
        fingerprint,
      });
      return inFlightMatch;
    }

    const lastQueuedAt = this.recentQueueFingerprints.get(fingerprint);
    if (lastQueuedAt && now - lastQueuedAt < QUEUE_DEDUPE_WINDOW_MS) {
      console.log('[LiveTracker] ⏭️ Deduped queue request (recent):', {
        documentId,
        trigger,
        fingerprint,
        ageMs: now - lastQueuedAt,
      });
      return;
    }

    const queueWork = async () => {
      const slidesToProcess = snapshots.map(snapshot => ({
        slideId: snapshot.slideId,
        slideIndex: snapshot.slideIndex,
        imageBase64: snapshot.imageBase64,
        xmlBase64: snapshot.pptxBase64,
        metadata: {
          layoutId: snapshot.layoutId,
          layoutName: snapshot.layoutName,
          masterTemplateId: snapshot.masterTemplateId,
          masterTemplateName: snapshot.masterTemplateName,
          slideCount: snapshots.length
        }
      }));

      // Resolve deck state and effective indexing decision before attempting backend send.
      const deckState = await this.storage.getDeckState(documentId);
      const deckTitle = deckState?.deckName || 'Untitled';
      const effectiveIndexing = this.resolveEffectiveDeckIndexing(deckState);

      if (!effectiveIndexing.enabled) {
        console.log('[LiveTracker] ⏸️ Skipping backend indexing send (indexing disabled):', {
          documentId,
          trigger,
          source: effectiveIndexing.source,
          globalEnabled: this.getGlobalIndexingEnabled(),
          deckOverrideEnabled: deckState?.indexingEnabled,
          slideIds: slidesToProcess.map(s => s.slideId),
        });
        return;
      }

      console.log('[LiveTracker] 📤 Queuing slides for processing:', {
        count: slidesToProcess.length,
        trigger,
        documentId,
        deckTitle,
        indexingDecisionSource: effectiveIndexing.source,
        slideIds: slidesToProcess.map(s => s.slideId),
        imageSizesKB: slidesToProcess.map(s => Math.round(s.imageBase64.length / 1024)),
        pptxSizesKB: slidesToProcess.map(s => s.xmlBase64 ? Math.round(s.xmlBase64.length / 1024) : 0)
      });

      try {
        // Mark all slides as in-flight/retrying before submitting to backend.
        for (const snapshot of snapshots) {
          const nextStatus: SlideIndexingStatus =
            isFailedDescription(snapshot.shortDescription) || snapshot.indexingStatus === 'failed'
              ? 'retrying'
              : 'in_flight';
          await this.storage.updateSlideIndexingStatus(documentId, snapshot.slideId, nextStatus);
        }

        const result = await this.backendClient.processSlides({
          documentId,
          deckTitle,
          slides: slidesToProcess,
          trigger
        });

        console.log('[LiveTracker] ✅ Backend response:', result);

        // Update lastProcessed timestamps
        const now = Date.now();
        for (const snapshot of snapshots) {
          await this.storage.saveSnapshot({
            ...snapshot,
            lastProcessed: now
          });
        }

        console.log(`[LiveTracker] 📤 Successfully queued ${slidesToProcess.length} slides for processing`);
      } catch (error) {
        // Revert transient in-flight status when queue submission fails.
        for (const snapshot of snapshots) {
          const fallbackStatus: SlideIndexingStatus =
            isFailedDescription(snapshot.shortDescription) || snapshot.indexingStatus === 'failed'
              ? 'failed'
              : 'pending';
          try {
            await this.storage.updateSlideIndexingStatus(documentId, snapshot.slideId, fallbackStatus);
          } catch (statusError) {
            console.warn(`[LiveTracker] Failed to restore indexing status for ${snapshot.slideId}:`, statusError);
          }
        }

        console.error('[LiveTracker] ❌ Error queuing slides for processing:', error);
        console.error('[LiveTracker] Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        throw error; // Re-throw so caller knows it failed
      }
    };

    const queuePromise = queueWork();
    this.inFlightQueueFingerprints.set(fingerprint, queuePromise);
    try {
      await queuePromise;
      this.recentQueueFingerprints.set(fingerprint, Date.now());
    } finally {
      this.inFlightQueueFingerprints.delete(fingerprint);
    }
  }

  /**
   * Register change detection handler
   */
  onChangeDetected(handler: ChangeDetectionHandler): void {
    this.changeHandlers.add(handler);
  }

  /**
   * Unregister change detection handler
   */
  offChangeDetected(handler: ChangeDetectionHandler): void {
    this.changeHandlers.delete(handler);
  }

  /**
   * Notify all handlers of change detection
   */
  private notifyChangeDetection(detection: ChangeDetection): void {
    this.changeHandlers.forEach(handler => {
      try {
        handler(detection);
      } catch (error) {
        console.error('Error in change detection handler:', error);
      }
    });
  }

  /**
   * Get current status
   */
  async getStatus(): Promise<LiveTrackerStatus> {
    try {
      const stats = await this.storage.getStats();
      const trackedDecks = await this.storage.getAllTrackedDecks();
      
      console.log('[LiveTracker] Status check:', {
        enabled: this.isEnabled,
        trackedDecks: trackedDecks.length,
        totalSnapshots: stats.snapshotCount
      });

      return {
        isEnabled: this.isEnabled,
        trackedDecks: trackedDecks.length,
        totalSnapshots: stats.snapshotCount,
        changesDetected: this.changesDetected,
        lastActivity: this.lastSelectionTime
      };
    } catch (error) {
      console.error('[LiveTracker] Error getting status:', error);
      return {
        isEnabled: this.isEnabled,
        trackedDecks: 0,
        totalSnapshots: 0,
        changesDetected: this.changesDetected,
        lastActivity: this.lastSelectionTime
      };
    }
  }

  async getState(): Promise<LiveTrackerStatus> {
    return this.getStatus();
  }

  /**
   * Clear all tracking data
   */
  async clearAll(clearOtherDatabases: boolean = true): Promise<void> {
    await this.storage.clearAll(clearOtherDatabases);
    this.changesDetected = 0;
    this.lastSelectedSlide = undefined;
    console.log('Cleared all tracking data');
  }

  /**
   * Cleanup deck tracking data older than a retention window.
   */
  async cleanupOldDeckTrackingData(
    retentionDays: number,
    options?: { excludedocumentIds?: string[] }
  ): Promise<{ deletedDecks: number; deletedSnapshots: number; keptDecks: number }> {
    return this.storage.cleanupOldDecks(retentionDays, {
      excludeDocumentIds: options?.excludedocumentIds,
    });
  }
}

// Singleton instance
export const liveTracker = new LiveTracker();

